import { every } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useDatabuilderJobList } from '..';
import { DatabuilderJobType, SyncType } from '../../../interfaces';
import queryClient from '../../queryClient';
import type { IDatabuilderJob } from '../../types';
import { databuilderJobQueryKeyFactory } from './constants';
import { useDatabuilderJobPollingContext } from './context';

type IPollArgs = {
	integrationId: string;
	type: SyncType;
	enabled?: boolean;
	page?: number;
};

type IPollResults = {
	databuilderJobs?: IDatabuilderJob[];
	isLoading: boolean;
	polling: boolean;
	startPolling: () => void;
	stopPolling: () => void;
};

export const usePollDatabuilderJobList = ({
	integrationId,
	type,
	enabled,
	page = 1,
}: IPollArgs): IPollResults => {
	const store = useDatabuilderJobPollingContext();
	// Check the current polling state from the context
	const polling = store.isPolling(integrationId, type);

	// Memoized function to update the polling state in the context
	const setPollingStateInStore = useCallback(
		(state: boolean) => {
			store.setPollingState(integrationId, type, state);
		},
		[integrationId, store, type]
	);

	const listFilter = useMemo(() => {
		// Determine the job types to filter based on the 'type' argument
		const jobTypeFilter =
			type === SyncType.PULL
				? [
						DatabuilderJobType.SCHEMA,
						DatabuilderJobType.DUMMY,
						DatabuilderJobType.METADATA,
					]
				: [DatabuilderJobType.PUSH];

		return {
			integration_id: integrationId,
			type__in: jobTypeFilter.join(','),
		};
	}, [integrationId, type]);

	// Memoized function to start polling
	const startPolling = useCallback(() => {
		setPollingStateInStore(true);
	}, [setPollingStateInStore]);

	// Memoized function to stop polling
	const stopPolling = useCallback(() => {
		setPollingStateInStore(false);
	}, [setPollingStateInStore]);

	const { data: databuilderJobs, isLoading } = useDatabuilderJobList({
		filters: listFilter,
		page,
		options: {
			// Enable the query if explicitly enabled OR if polling is active for this integration/type
			enabled: enabled || polling,
			// Configure refetching interval and conditions
			refetchInterval: (query) => {
				const { data } = query.state;

				// Determine if polling should stop
				const shouldStopPolling =
					!data?.results?.length || // Stop if no results
					every(
						// Stop if all jobs are in a terminal state
						data?.results,
						(job) =>
							job.failed || job.completed || job.terminated || job.timed_out
					);

				if (shouldStopPolling) {
					// If polling is currently active, explicitly stop it
					if (polling) {
						stopPolling();
					}
					return false; // Signal React Query to stop refetching
				}

				// If polling is not active but should be (e.g., jobs are running), start it
				if (!polling) {
					startPolling();
				}

				return 4000; // Continue polling every 4 seconds
			},
			// Keep polling even if the window/tab is not focused
			refetchIntervalInBackground: true,
			// Prevent garbage collection of this query while polling is active (use cacheTime for React Query v4)
			gcTime: polling ? Infinity : undefined,
		},
	});

	return {
		databuilderJobs: databuilderJobs?.results,
		isLoading,
		polling,
		startPolling,
		stopPolling,
	};
};

// Force immediate refetching for synchronous UI actions without waiting for polling.
// NOTE: Invalidates all databuilder job queries for now, as the specific list key factory signature is unclear.
export const forceFetchJobs = () => {
	// Determine the query key to invalidate
	// Reverted change: Always invalidate all for now to avoid type errors
	const queryKeyToInvalidate = databuilderJobQueryKeyFactory.all();

	// Invalidate the determined queries
	queryClient.invalidateQueries({
		queryKey: queryKeyToInvalidate,
	});
};
